/**
 * Peak Hours Heat Map
 * Hour x Day heat map showing optimal times for updates/events
 * Solis Games - Phase 2.2 V3
 */

import React from 'react';
import Icon from '../../../components/AppIcon';

const PeakHoursHeatMap = ({ data, loading }) => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const hours = Array.from({ length: 24 }, (_, i) => i); // 0-23

  if (loading) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading peak hours data...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.heatMap || Object.keys(data.heatMap).length === 0) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="text-center py-12">
          <Icon name="Clock" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Peak Hours Data Yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Peak hours tracking will begin once players start actively playing your game.
            Check back after you have at least 7 days of session data.
          </p>
        </div>
      </div>
    );
  }

  // Get max value for normalization
  const maxPlayers = Math.max(...Object.values(data.heatMap).flat());

  const getHeatColor = (value) => {
    if (!value || value === 0) return 'bg-muted/30';
    const intensity = value / maxPlayers;
    if (intensity >= 0.8) return 'bg-red-500';
    if (intensity >= 0.6) return 'bg-orange-500';
    if (intensity >= 0.4) return 'bg-yellow-500';
    if (intensity >= 0.2) return 'bg-green-500';
    return 'bg-blue-500';
  };

  const getHeatOpacity = (value) => {
    if (!value || value === 0) return '';
    const intensity = value / maxPlayers;
    if (intensity >= 0.8) return 'opacity-100';
    if (intensity >= 0.6) return 'opacity-80';
    if (intensity >= 0.4) return 'opacity-60';
    if (intensity >= 0.2) return 'opacity-40';
    return 'opacity-20';
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="Clock" size={24} className="text-primary" />
              Peak Hours Intelligence
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Hour x Day heat map showing when your players are most active
            </p>
          </div>
          {data.peakHour && (
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">{formatHour(data.peakHour.hour)}</div>
              <div className="text-sm text-muted-foreground">{data.peakHour.day}</div>
              <div className="text-xs text-muted-foreground mt-1">Peak Time</div>
            </div>
          )}
        </div>
      </div>

      {/* Heat Map */}
      <div className="p-6 overflow-x-auto">
        <div className="inline-block min-w-full">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="p-2 text-left text-xs font-semibold text-muted-foreground sticky left-0 bg-card">
                  Day / Hour
                </th>
                {hours.map(hour => (
                  <th key={hour} className="p-1 text-center text-xs font-semibold text-muted-foreground min-w-[40px]">
                    {hour % 6 === 0 ? formatHour(hour).split(' ')[0] : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((day, dayIdx) => (
                <tr key={day} className="border-t border-border">
                  <td className="p-2 text-sm font-medium text-foreground sticky left-0 bg-card">
                    {day}
                  </td>
                  {hours.map(hour => {
                    const value = data.heatMap[day]?.[hour] || 0;
                    const isPeak = data.peakHour && data.peakHour.day === day && data.peakHour.hour === hour;

                    return (
                      <td key={hour} className="p-1">
                        <div
                          className={`w-8 h-8 rounded ${getHeatColor(value)} ${getHeatOpacity(value)} flex items-center justify-center text-xs font-semibold text-white hover:scale-110 transition-transform cursor-pointer ${
                            isPeak ? 'ring-2 ring-primary ring-offset-2 ring-offset-card' : ''
                          }`}
                          title={`${day} ${formatHour(hour)}: ${value} players`}
                        >
                          {value > 0 && isPeak && (
                            <Icon name="Star" size={16} className="text-white" />
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="px-6 pb-4">
        <div className="flex items-center gap-4 text-xs">
          <span className="text-muted-foreground font-semibold">Player Activity:</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 opacity-20 rounded"></div>
            <span className="text-muted-foreground">Low</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 opacity-40 rounded"></div>
            <span className="text-muted-foreground">Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 opacity-60 rounded"></div>
            <span className="text-muted-foreground">High</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-orange-500 opacity-80 rounded"></div>
            <span className="text-muted-foreground">Very High</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 opacity-100 rounded"></div>
            <span className="text-muted-foreground">Peak</span>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="p-6 border-t border-border bg-primary/5">
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Lightbulb" size={18} className="text-yellow-500" />
            Timing Recommendations
          </h4>
          <div className="space-y-3">
            {data.recommendations.map((rec, idx) => (
              <div key={idx} className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={rec.icon || 'Calendar'} size={20} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground text-sm mb-1">{rec.title}</div>
                    <div className="text-sm text-muted-foreground">{rec.message}</div>
                    {rec.time && (
                      <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
                        <Icon name="Clock" size={14} className="text-primary" />
                        <span className="text-xs font-semibold text-primary">{rec.time}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Patterns */}
      {data.patterns && (
        <div className="p-6 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-4">Activity Patterns</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.patterns.busiestDay && (
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="text-xs text-muted-foreground mb-1">Busiest Day</div>
                <div className="text-lg font-bold text-foreground">{data.patterns.busiestDay.day}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {data.patterns.busiestDay.players.toLocaleString()} avg players
                </div>
              </div>
            )}

            {data.patterns.quietestDay && (
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="text-xs text-muted-foreground mb-1">Quietest Day</div>
                <div className="text-lg font-bold text-foreground">{data.patterns.quietestDay.day}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {data.patterns.quietestDay.players.toLocaleString()} avg players
                </div>
              </div>
            )}

            {data.patterns.weekendEffect && (
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="text-xs text-muted-foreground mb-1">Weekend Effect</div>
                <div className="text-lg font-bold text-foreground">
                  {data.patterns.weekendEffect > 0 ? '+' : ''}{data.patterns.weekendEffect}%
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {data.patterns.weekendEffect > 0 ? 'More' : 'Less'} activity on weekends
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PeakHoursHeatMap;
